import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { metaFor } from "@/lib/notifMeta";
import NotificationItem from "@/components/NotificationItem";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotificationStream } from "@/hooks/useNotificationStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map notification types → configured sound URL key from /settings/public
const TYPE_SOUND_KEY = {
  ride_request: "sound_ride_request_url",
  freelancer_invite: "sound_freelancer_invite_url",
  freelancer_same_day: "sound_freelancer_same_day_url",
  live_invite: "sound_freelancer_invite_url",
};

export default function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnreadLocal] = useState(0);
  const [open, setOpen] = useState(false);
  const [sounds, setSounds] = useState({});
  const { unread: streamUnread, lastNotification, setUnread: setStreamUnread } = useNotificationStream();

  const seenIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);
  const audioRefs = useRef({});
  const staleLoadUntilRef = useRef(0); // ignore load() responses issued before this timestamp

  // Sync SSE stream unread → local counter
  useEffect(() => { setUnreadLocal(streamUnread); }, [streamUnread]);
  const setUnread = (v) => {
    const value = typeof v === "function" ? v(unread) : v;
    setUnreadLocal(value);
    setStreamUnread(value);
  };

  // React to real-time pushes: play sound and prepend the fresh notification.
  useEffect(() => {
    if (!lastNotification) return;
    setItems((prev) => {
      if (prev.some((n) => n.notif_id === lastNotification.notif_id)) return prev;
      return [lastNotification, ...prev].slice(0, 50);
    });
    if (!seenIdsRef.current.has(lastNotification.notif_id)) {
      seenIdsRef.current.add(lastNotification.notif_id);
      playSoundFor(lastNotification.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNotification]);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => {
      setSounds({
        sound_ride_request_url: r.data.sound_ride_request_url || "",
        sound_freelancer_invite_url: r.data.sound_freelancer_invite_url || "",
        sound_freelancer_same_day_url: r.data.sound_freelancer_same_day_url || "",
      });
    }).catch(() => {});
  }, []);

  const playSoundFor = (type) => {
    const key = TYPE_SOUND_KEY[type];
    if (!key) return;
    const url = sounds[key];
    if (!url) return;
    try {
      let el = audioRefs.current[key];
      if (!el || el.src !== url) {
        el = new Audio(url);
        el.preload = "auto";
        el.volume = 0.85;
        audioRefs.current[key] = el;
      }
      el.currentTime = 0;
      el.play().catch(() => {});
    } catch {}
  };

  const load = async () => {
    const issuedAt = Date.now();
    try {
      const res = await axios.get(`${API}/notifications`);
      // Drop responses that were issued BEFORE the last mark-all — they carry stale unread counts.
      if (issuedAt < staleLoadUntilRef.current) return;
      const list = res.data.notifications || [];
      setItems(list);
      setUnread(res.data.unread || 0);
      if (initialLoadRef.current) {
        list.forEach((n) => seenIdsRef.current.add(n.notif_id));
        initialLoadRef.current = false;
        return;
      }
      const newOnes = list.filter((n) => !seenIdsRef.current.has(n.notif_id) && !n.read);
      newOnes.forEach((n) => { seenIdsRef.current.add(n.notif_id); playSoundFor(n.type); });
      list.forEach((n) => seenIdsRef.current.add(n.notif_id));
    } catch (err) {
      if (err.response?.status !== 401) console.error("[Notif] load failed", err);
    }
  };

  useEffect(() => {
    // Initial full-list fetch to populate the popover. SSE takes over live
    // updates from here (unread counter + new items). We keep a slow 45s
    // safety poll as a fallback in case the SSE reconnects fail silently.
    load();
    const interval = setInterval(load, 45000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sounds]);

  // Refetch the full list when opening the popover so we don't show stale data.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open]);
  const markAll = async () => {
    // Optimistic: clear badge + local read state instantly.
    staleLoadUntilRef.current = Date.now();
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await axios.post(`${API}/notifications/read-all`);
    } catch {}
    load();
  };

  const openNotif = (n) => {
    const meta = metaFor(n.type);
    if (!n.read) {
      setItems((prev) => prev.map((x) => x.notif_id === n.notif_id ? { ...x, read: true } : x));
      axios.post(`${API}/notifications/${n.notif_id}/read`).catch(() => {});
    }
    try {
      const target = meta.route(n);
      if (target) navigate(target);
    } finally {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markAll(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notif-bell-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-panel">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <div className="font-bold">{t("notifications")}</div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="notif-mark-all">
                {t("markAllRead")}
              </button>
            )}
            <button
              onClick={() => { setOpen(false); navigate("/settings/notifications"); }}
              className="text-xs text-muted-foreground hover:underline"
              data-testid="notif-open-settings"
              title="Preferências"
            >
              Preferências
            </button>
          </div>
        </div>
        <div className="h-96 overflow-y-auto" data-testid="notif-scroll">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">{t("noNotifs")}</div>
          )}
          {items.map((n) => (
            <NotificationItem key={n.notif_id} n={n} onClick={openNotif} dense />
          ))}
        </div>
        <button
          onClick={() => { setOpen(false); navigate("/notifications"); }}
          className="w-full py-2.5 text-sm font-semibold text-primary hover:bg-muted/50 border-t border-border transition-colors"
          data-testid="notif-see-all"
        >
          Ver todas as notificações
        </button>
      </PopoverContent>
    </Popover>
  );
}
